import type { Context } from '@opentelemetry/api';
import type { ReadableSpan, Span, SpanProcessor } from '@opentelemetry/sdk-trace-base';

const HTTP_METHODS = new Set(['GET', 'HEAD', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS']);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LONG_HEX = /^[0-9a-f]{16,}$/i;
const DIGITS = /^\d+$/;

function looksLikeId(segment: string): boolean {
  if (UUID.test(segment) || LONG_HEX.test(segment) || DIGITS.test(segment)) return true;
  // cuid/ulid/nanoid-style identifiers: long, and mixing digits into letters.
  return segment.length >= 20 && /\d/.test(segment) && /[a-z]/i.test(segment);
}

/** `/v1/chat/abc123…/messages` -> `/v1/chat/:id/messages`, so span names stay low-cardinality. */
export function normalizePath(pathname: string): string {
  return pathname
    .split('/')
    .map((segment) => (looksLikeId(segment) ? ':id' : segment))
    .join('/');
}

function stringAttribute(span: ReadableSpan, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = span.attributes[key];
    if (typeof value === 'string') return value;
  }
  return undefined;
}

/** The name an HTTP client span should have: `GET /v1/chat/:id`. Undefined when it isn't a bare-method HTTP span. */
export function httpSpanName(span: ReadableSpan): string | undefined {
  const method = span.name.replace(/^HTTP /, '');
  if (!HTTP_METHODS.has(method)) return undefined;

  const url = stringAttribute(span, 'url.full', 'http.url');
  if (!url) return undefined;
  try {
    return `${method} ${normalizePath(new URL(url, window.location.href).pathname)}`;
  } catch {
    return undefined;
  }
}

export type BrowserSpanProcessorOptions = {
  /** Page loads emit one `resourceFetch` span per asset (often 100+). Off by default: they are noise at scale. */
  recordResourceSpans: boolean;
};

/**
 * In front of the batching processor:
 * - names HTTP client spans after their path (`GET /v1/chat/:id`), not just the method, using a normalized path so
 *   the name never carries ids;
 * - drops per-asset `resourceFetch` spans before they are queued, so a page load cannot fill the queue and push out
 *   the root `documentLoad` span, which ends last.
 */
export class BrowserSpanProcessor implements SpanProcessor {
  constructor(
    private readonly inner: SpanProcessor,
    private readonly options: BrowserSpanProcessorOptions
  ) {}

  onStart(span: Span, parentContext: Context): void {
    const name = httpSpanName(span);
    if (name) span.updateName(name);
    this.inner.onStart(span, parentContext);
  }

  onEnd(span: ReadableSpan): void {
    if (!this.options.recordResourceSpans && span.name === 'resourceFetch') return;
    this.inner.onEnd(span);
  }

  forceFlush(): Promise<void> {
    return this.inner.forceFlush();
  }

  shutdown(): Promise<void> {
    return this.inner.shutdown();
  }
}
